import { Button, Card, Empty, Space, Table, Tag } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '@/services/api.service';
import EducationPageChrome from '@/pages/education/components/EducationPageChrome';

type History = {
  enrollments: Array<{ id: string; session_id: string; grade_level_id: string; status: string; enrollment_no: string }>;
  allocations: Array<{ id: string; section_id: string; effective_on: string; ended_on?: string; is_current: number }>;
  withdrawals: Array<{ id: string; status: string; reason_category: string }>;
  certificates: Array<{ id: string; tc_number?: string; status: string }>;
};

export default function AcademicHistoryPage() {
  const { t } = useTranslation('education');
  const { id } = useParams();
  const navigate = useNavigate();
  const query = useQuery({
    queryKey: ['education-history', id],
    queryFn: () => api.get<History>(`/education/students/${id}/enrollment-history`),
  });
  const history = query.data?.data;
  const enrollmentCount = history?.enrollments?.length ?? 0;

  return (
    <EducationPageChrome
      title={t('academicHistory')}
      description={t('academicHistoryIntro')}
      tags={<Tag>{enrollmentCount} {t('enrollmentsTitle')}</Tag>}
      extra={<Button onClick={() => navigate('/education/enrollments')}>{t('backToEnrollments')}</Button>}
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Card size="small" title={t('enrollmentsTitle')}>
          <Table
            rowKey="id"
            loading={query.isLoading}
            dataSource={history?.enrollments || []}
            pagination={{ pageSize: 10, showSizeChanger: true }}
            locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} /> }}
            columns={[
              { title: t('enrollmentNo'), dataIndex: 'enrollment_no' },
              { title: t('session'), dataIndex: 'session_id', ellipsis: true },
              { title: t('grade'), dataIndex: 'grade_level_id', ellipsis: true },
              {
                title: t('status'),
                dataIndex: 'status',
                width: 140,
                render: (value: string) => <Tag>{value}</Tag>,
              },
            ]}
          />
        </Card>
        <Card size="small" title={t('section', { defaultValue: 'Sections' })}>
          <Table
            rowKey="id"
            loading={query.isLoading}
            dataSource={history?.allocations || []}
            pagination={{ pageSize: 10, showSizeChanger: true }}
            locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} /> }}
            columns={[
              { title: t('section'), dataIndex: 'section_id', ellipsis: true },
              { title: t('effectiveOn'), dataIndex: 'effective_on', width: 140 },
              { title: t('endedOn'), dataIndex: 'ended_on', width: 140, render: (v: string) => v || '—' },
            ]}
          />
        </Card>
        <Card size="small" title={t('withdrawal')}>
          <Table
            rowKey="id"
            loading={query.isLoading}
            dataSource={history?.withdrawals || []}
            pagination={{ pageSize: 10, showSizeChanger: true }}
            locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} /> }}
            columns={[
              {
                title: t('status'),
                dataIndex: 'status',
                render: (value: string) => <Tag>{value}</Tag>,
              },
              { title: t('reason'), dataIndex: 'reason_category' },
            ]}
          />
        </Card>
        <Card size="small" title={t('transferCertificate')}>
          <Table
            rowKey="id"
            loading={query.isLoading}
            dataSource={history?.certificates || []}
            pagination={{ pageSize: 10, showSizeChanger: true }}
            locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} /> }}
            columns={[
              { title: t('transferCertificate'), dataIndex: 'tc_number', render: (v: string) => v || '—' },
              {
                title: t('status'),
                dataIndex: 'status',
                render: (value: string) => <Tag>{value}</Tag>,
              },
            ]}
          />
        </Card>
      </Space>
    </EducationPageChrome>
  );
}
